'use client'; // TODO: why?

import Image from 'next/image';
import { useTenantAndVariant } from '@multitenancy/TenantAndVariantContext';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { HttpTypes } from '@medusajs/types';

interface CategoryWithImage extends HttpTypes.StoreProductCategory {
  image_url?: string;
  size?: 'large' | 'small';
}

interface CategoryGridProps {
  categories: CategoryWithImage[];
}

export function CategoryGrid({ categories }: CategoryGridProps) {
  const { featureToggles } = useTenantAndVariant();
  const isBoldText = featureToggles.categoryTextStyle === 'bold';
  const layout = featureToggles.categoryGridLayout;

  // Don't render if layout is hidden or no categories
  if (layout === 'hidden' || !categories || categories.length === 0) {
    return null;
  }

  // Split categories for contained layout
  const largeCategories = categories.filter(c => c.size === 'large');
  const smallCategories = categories.filter(c => c.size === 'small');

  if (layout === 'contained' && largeCategories.length >= 2) {
    const firstLarge = largeCategories[0];
    const secondLarge = largeCategories[1];

    return (
      <section className="py-16 bg-white">
        <div className="w-full px-0">
          <h2 className="text-3xl md:text-4xl font-light text-center mb-12 tracking-[0.15em] uppercase text-[#2B2C2D] px-4">
            Shop by Category
          </h2>

          {/* Contained Layout - Two full-width sections */}
          <div className="space-y-0">
            {/* First Large Category Section */}
            <div className="relative overflow-hidden h-96 w-full">
              {/* Background Image */}
              {firstLarge.image_url && (
                <Image
                  src={firstLarge.image_url}
                  alt={firstLarge.name}
                  fill
                  className="object-cover"
                />
              )}
              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/60"></div>

              {/* Category Title */}
              <div className="absolute top-8 left-8">
                <h3 className={`
                  tracking-wider uppercase transition-all duration-500 text-white
                  ${isBoldText
                    ? 'font-black text-5xl md:text-7xl text-shadow-bold opacity-90'
                    : 'font-semibold text-4xl md:text-5xl'
                  }
                `}>
                  {firstLarge.name}
                </h3>
              </div>

              {/* Grid of small tiles */}
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <div className="grid grid-cols-4 gap-4 w-full max-w-6xl">
                  {smallCategories.slice(0, 4).map((category, index) => (
                    <LocalizedClientLink
                      key={category.id || index}
                      href={`/categories/${category.handle}`}
                      className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-all duration-500"
                      style={{ aspectRatio: '3/4' }}
                    >
                      {/* Small tile background image */}
                      <div className="absolute inset-0">
                        {category.image_url ? (
                          <Image
                            src={category.image_url}
                            alt={category.name}
                            fill
                            className="object-cover transition-transform duration-700 group-hover:scale-110 opacity-80"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30" />
                        )}
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-500"></div>
                      </div>

                      {/* Category Name */}
                      <div className="absolute inset-0 flex items-end justify-start p-4">
                        <h3 className={`
                          tracking-wider uppercase transition-all duration-500 text-white
                          ${isBoldText
                            ? 'font-black text-xl md:text-2xl text-shadow-bold opacity-90'
                            : 'font-semibold text-base md:text-lg'
                          }
                          group-hover:scale-110
                        `}>
                          {category.name}
                        </h3>
                      </div>

                      {/* Decorative Border on Hover */}
                      <div className="absolute inset-0 border-2 border-white/0 group-hover:border-white/40 rounded-lg transition-all duration-500"></div>
                    </LocalizedClientLink>
                  ))}
                </div>
              </div>
            </div>

            {/* Second Large Category Section */}
            <div className="relative overflow-hidden h-96 w-full">
              {/* Background Image */}
              {secondLarge.image_url && (
                <Image
                  src={secondLarge.image_url}
                  alt={secondLarge.name}
                  fill
                  className="object-cover"
                />
              )}
              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/60"></div>

              {/* Category Title */}
              <div className="absolute top-8 right-8">
                <h3 className={`
                  tracking-wider uppercase transition-all duration-500 text-white text-right
                  ${isBoldText
                    ? 'font-black text-5xl md:text-7xl text-shadow-bold opacity-90'
                    : 'font-semibold text-4xl md:text-5xl'
                  }
                `}>
                  {secondLarge.name}
                </h3>
              </div>

              {/* Grid of remaining small tiles */}
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <div className="grid grid-cols-4 gap-4 w-full max-w-6xl">
                  {smallCategories.slice(4, 8).map((category, index) => (
                    <LocalizedClientLink
                      key={category.id || index}
                      href={`/categories/${category.handle}`}
                      className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-all duration-500"
                      style={{ aspectRatio: '3/4' }}
                    >
                      {/* Small tile background image */}
                      <div className="absolute inset-0">
                        {category.image_url ? (
                          <Image
                            src={category.image_url}
                            alt={category.name}
                            fill
                            className="object-cover transition-transform duration-700 group-hover:scale-110 opacity-80"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30" />
                        )}
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-500"></div>
                      </div>

                      {/* Category Name */}
                      <div className="absolute inset-0 flex items-end justify-start p-4">
                        <h3 className={`
                          tracking-wider uppercase transition-all duration-500 text-white
                          ${isBoldText
                            ? 'font-black text-xl md:text-2xl text-shadow-bold opacity-90'
                            : 'font-semibold text-base md:text-lg'
                          }
                          group-hover:scale-110
                        `}>
                          {category.name}
                        </h3>
                      </div>

                      {/* Decorative Border on Hover */}
                      <div className="absolute inset-0 border-2 border-white/0 group-hover:border-white/40 rounded-lg transition-all duration-500"></div>
                    </LocalizedClientLink>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CSS for text shadow */}
        <style jsx>{`
          .text-shadow-bold {
            text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.9), 
                         0 0 15px rgba(0, 0, 0, 0.7),
                         0 0 30px rgba(0, 0, 0, 0.5);
            letter-spacing: 0.05em;
          }
        `}</style>
      </section>
    );
  }

  // Original tiles layout
  return (
    <section className="py-16 bg-white">
  <div className="container">
        <h2 className="text-3xl md:text-4xl font-light text-center mb-12 tracking-[0.15em] uppercase text-[#2B2C2D]">
          Shop by Category
        </h2>

        {/* 4x4 Grid Container */}
        <div className="grid grid-cols-4 gap-4 max-w-6xl mx-auto">
          {categories.map((category, index) => (
            <LocalizedClientLink
              key={category.id || index}
              href={`/categories/${category.handle}`}
              className={`
              group relative overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-all duration-500
              ${category.size === 'large' ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1'}
            `}
              style={{ aspectRatio: '3/4' }}
            >
              {/* Background Image or Fallback */}
              <div className="absolute inset-0">
                {category.image_url ? (
                  <Image
                    src={category.image_url}
                    alt={category.name}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20" />
                )}
                {/* Solid Overlay */}
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/60 transition-all duration-500"></div>
              </div>              {/* Category Name */}
              <div className="absolute inset-0 flex items-end justify-start p-6">
                <div className="text-left">
                  <h3 className={`
                    tracking-wider uppercase transition-all duration-500
                    ${isBoldText
                      ? `font-black ${category.size === 'large' ? 'text-6xl mb-4' : 'text-2xl md:text-3xl'} text-shadow-bold text-white opacity-90`
                      : `font-semibold ${category.size === 'large' ? 'text-4xl mb-2' : 'text-lg md:text-xl'} text-white`
                    }
                    group-hover:scale-110
                  `}>
                    {category.name}
                  </h3>

                  {category.size === 'large' && (
                    <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className={`inline-block px-6 py-3 rounded-full transition-all ${isBoldText ? 'font-black text-lg bg-white text-black opacity-95' : 'font-medium text-sm bg-white text-black'}`}>
                        Explore Now →
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Decorative Border on Hover */}
              <div className="absolute inset-0 border-2 border-white/0 group-hover:border-white/40 rounded-lg transition-all duration-500"></div>
            </LocalizedClientLink>
          ))}
        </div>

        {/* Optional: Mobile responsive hint */}
        <style jsx>{`
          @media (max-width: 768px) {
            .grid {
              grid-template-columns: repeat(2, 1fr);
            }
            .col-span-2 {
              grid-column: span 2;
            }
            .row-span-2 {
              grid-row: span 2;
            }
            .col-span-1 {
              grid-column: span 1;
            }
            .row-span-1 {
              grid-row: span 1;
            }
          }
          .text-shadow-bold {
            text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.9), 
                         0 0 15px rgba(0, 0, 0, 0.7),
                         0 0 30px rgba(0, 0, 0, 0.5);
            letter-spacing: 0.05em;
          }
        `}</style>
      </div>
    </section>
  );
}
